import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import { UserPlus, AlertCircle } from 'lucide-react';

const S = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-bg-primary)',
    padding: 'var(--space-lg)',
    background: 'radial-gradient(ellipse at center, var(--color-bg-secondary) 0%, var(--color-bg-primary) 70%)',
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(212, 175, 55, 0.15)',
    boxShadow: 'var(--shadow-lg)',
    overflow: 'hidden',
  },
  header: {
    padding: 'var(--space-2xl) var(--space-xl) var(--space-lg)',
    textAlign: 'center',
    borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
  },
  brand: {
    fontFamily: 'var(--font-display)',
    fontSize: '2rem',
    fontWeight: 700,
    color: 'var(--color-primary)',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    margin: 0,
    textShadow: '0 0 30px rgba(212, 175, 55, 0.3)',
  },
  subtitle: {
    fontFamily: 'var(--font-display)',
    fontSize: '0.8rem',
    color: 'var(--color-text-muted)',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    marginTop: 'var(--space-xs)',
    marginBottom: 0,
  },
  body: { padding: 'var(--space-xl)' },
  form: { display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-sm)',
    padding: 'var(--space-sm) var(--space-md)',
    backgroundColor: 'rgba(231, 76, 60, 0.1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(231, 76, 60, 0.2)',
    color: 'var(--color-danger)',
    fontSize: '0.85rem',
  },
  free: {
    padding: 'var(--space-sm) var(--space-md)',
    backgroundColor: 'rgba(46, 204, 113, 0.08)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(46, 204, 113, 0.15)',
    color: 'var(--color-success)',
    fontSize: '0.82rem',
    textAlign: 'center',
    fontFamily: 'var(--font-display)',
    letterSpacing: '0.03em',
  },
  footer: {
    padding: 'var(--space-md) var(--space-xl) var(--space-xl)',
    textAlign: 'center',
  },
  footerText: {
    fontSize: '0.8rem',
    color: 'var(--color-text-muted)',
    margin: 0,
  },
  link: {
    color: 'var(--color-primary)',
    textDecoration: 'none',
    fontWeight: 600,
  },
};

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== passwordConfirmation) {
      setError('Le password non corrispondono.');
      return;
    }

    setLoading(true);
    try {
      await register({
        name,
        email,
        password,
        password_confirmation: passwordConfirmation,
      });
      navigate('/admin');
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors) {
        const firstError = Object.values(data.errors)[0];
        setError(Array.isArray(firstError) ? firstError[0] : firstError);
      } else {
        setError(data?.message || 'Errore durante la registrazione.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.header}>
          <h1 style={S.brand}>PickBoxGame</h1>
          <p style={S.subtitle}>Diventa organizzatore</p>
        </div>

        <div style={S.body}>
          <form onSubmit={handleSubmit} style={S.form}>
            {error && (
              <div style={S.error}>
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <div style={S.free}>
              La prima partita e' gratuita per provare!
            </div>

            <Input
              label="Nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Il tuo nome"
              required
            />

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@esempio.it"
              required
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimo 8 caratteri"
              required
            />

            <Input
              label="Conferma password"
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              placeholder="Ripeti la password"
              required
            />

            <Button
              variant="primary"
              size="lg"
              type="submit"
              loading={loading}
              style={{ width: '100%', marginTop: 'var(--space-sm)' }}
            >
              <UserPlus size={18} />
              Registrati
            </Button>
          </form>
        </div>

        <div style={S.footer}>
          <p style={S.footerText}>
            Hai gia' un account? <Link to="/login" style={S.link}>Accedi</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
